import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  BookOpen,
  User,
  LogOut,
  X,
  ChevronLeft,
  ChevronRight,
  FileText
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import DocumentSideModal from "../documents/DocumentSideModal";

interface DashboardSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSectionChange: (section: 'dashboard' | 'resources' | 'account') => void;
  activeSection: string;
}

const menuItems = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    section: 'dashboard',
    ariaLabel: "Navigate to dashboard",
  },
  {
    icon: FileText,
    label: "Documents",
    modal: 'documents',
    ariaLabel: "Manage documents",
  },
  {
    icon: BookOpen,
    label: "Resources",
    section: 'resources',
    ariaLabel: "View resources",
  },
  {
    icon: User,
    label: "My Account",
    section: 'account',
    ariaLabel: "Manage account settings",
  },
];

const DashboardSidebar = ({ 
  isOpen, 
  onClose, 
  onSectionChange, 
  activeSection 
}: DashboardSidebarProps) => {
  const location = useLocation();
  const { signOut } = useAuth();
  const { toast } = useToast();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account.",
      });
      onClose();
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        variant: "destructive",
        title: "Error signing out",
        description: "There was a problem signing you out. Please try again.",
      });
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleMenuItemClick = (item: typeof menuItems[0]) => {
    if (item.modal === 'documents') {
      setIsDocumentModalOpen(true);
    } else if (item.section) {
      onSectionChange(item.section as any);
    }
    onClose();
  };

  return (
    <>
      <aside
        ref={sidebarRef}
        className={cn(
          "fixed left-0 top-0 h-screen bg-card/95 backdrop-blur-lg border-r border-border/10 z-50",
          "transform transition-all duration-300 ease-in-out lg:transform-none",
          "focus:outline-none",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          isCollapsed ? "w-20" : "w-64"
        )}
        aria-label="Dashboard navigation"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="p-6 flex items-center justify-between border-b border-border/10">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <img src="/navisa-logo.svg" alt="Navisa" className="h-8 w-auto" />
              <span className="text-xl font-bold text-white">Navisa</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleCollapse}
              className="hidden lg:flex"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronLeft className="h-5 w-5 text-muted-foreground" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="lg:hidden"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4" role="navigation">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.section 
                ? activeSection === item.section 
                : location.pathname === item.path;

              return (
                <li key={item.section || item.modal}>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors",
                      "hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-accent",
                      isActive && "bg-accent/10 text-accent",
                      isCollapsed && "justify-center px-2"
                    )}
                    onClick={() => handleMenuItemClick(item)}
                    aria-label={item.ariaLabel}
                    aria-current={isActive ? "page" : undefined}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <Icon className="h-5 w-5" />
                    {!isCollapsed && item.label}
                  </Button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="absolute bottom-8 left-0 right-0 px-4">
          <Button
            variant="ghost"
            className={cn(
              "w-full flex items-center gap-3 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-accent hover:bg-accent/5",
              isCollapsed && "justify-center px-2"
            )}
            onClick={handleSignOut}
            aria-label="Sign out of your account"
            title={isCollapsed ? "Sign out" : undefined}
          >
            <LogOut className="h-5 w-5" />
            {!isCollapsed && "Sign out"}
          </Button>
        </div>
      </aside>

      <DocumentSideModal
        open={isDocumentModalOpen}
        onOpenChange={setIsDocumentModalOpen}
      />
    </>
  );
};

export default DashboardSidebar;